import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap, take } from "rxjs/operators";
import { AngularFirestore, DocumentData, QueryFn } from '@angular/fire/firestore';
import { DataService } from '../data.service';
import { InventoryItemDTO } from 'src/app/models/results/inventory-item-dto';
import { AngularFireAuth } from '@angular/fire/auth';
import { environment } from 'src/environments/environment';
import { ResultConfigViewModel } from 'src/app/models/results/result-config-view-model';
import { SynnexInventoryItem } from 'src/app/models/data/synnex/synnex-inventory-item';
import { constants } from 'src/environments/constants';
import { ILocaleInfo, UtilityService } from '../../utility.service';

@Injectable({
  providedIn: 'root'
})
export class SynnexDataService implements DataService {

  constructor(
    private store: AngularFirestore,
    private auth: AngularFireAuth,
    private utilityService: UtilityService) { }

  updateResultConfigItem(item: ResultConfigViewModel): Promise<void> {
    return new Promise((resolve, reject) => {
      resolve();
    });
  }

  getAdminEmailAddresses(): Observable<string[]> {
    return of([]);
  }

  executeDatabaseQuery(): Observable<InventoryItemDTO[]> {
    const localeInfo: ILocaleInfo = this.utilityService.getLocaleInfo();

    const queryFn: QueryFn<DocumentData> = ref => ref.where('Locality', '==', localeInfo.localeName).where('Deleted', '==', false);

    return this.auth.user.pipe(
      take(1),
      switchMap(async user => {
        if (!user) {
          await this.auth.signInWithEmailAndPassword(environment.firebaseUserEmail, environment.firebase.apiKey);
        }
      }),
      switchMap(() => this.store.collection<SynnexInventoryItem>(`${constants.FIRESTORE_INVENTORY_COLLECTION}_synnex`, queryFn).valueChanges()),
      take(1),
      map((synnexItems: SynnexInventoryItem[]) => {
        return synnexItems.map(synnexItem => this.mapDTO(synnexItem));
      })
    );
  }

  mapDTO(synnexItem: SynnexInventoryItem): InventoryItemDTO {
    return Object.assign(new InventoryItemDTO(), {
      id: synnexItem.ID,
      hasBacklitKeyboard: synnexItem.BacklitKeyboard,
      hasFingerprintReader: synnexItem.HasFingerprintReader,
      hasPrivacyScreen: synnexItem.HasPrivacyScreen,
      hasStylus: synnexItem.HasStylus,
      cpuModel: synnexItem.CpuModel,
      diskDriveSize: synnexItem.DiskDriveSize,
      diskDriveUnit: synnexItem.DiskDriveUnit,
      diskDriveType: synnexItem.DiskDriveType,
      hasTouchscreen: synnexItem.HasTouchscreen,
      imageLargeLocation: synnexItem.ImageLarge,
      isEducation: synnexItem.IsEducation,
      isParallelsCapable: synnexItem.IsParallelsCapable,
      listPrice: SynnexInventoryItem.getPrice(synnexItem),
      locality: synnexItem.Locality,
      manufactureName: synnexItem.ManufactureName,
      manufacturePart: synnexItem.ManufacturePart,
      materialID: synnexItem.MaterialID,
      memorySize: synnexItem.MemorySize,
      memoryUnit: synnexItem.MemoryUnit,
      name: synnexItem.ProductDescShort.split(' - ')[0],
      productDescLong: synnexItem.ProductDescLong,
      productDescShort: synnexItem.ProductDescShort,
      productLine: synnexItem.ProductLine,
      productType: synnexItem.ProductType,
      formattedScreenSize: synnexItem.ScreenSize,
      screenSize: SynnexInventoryItem.getScreenSize(synnexItem),
      showInResults: synnexItem.ShowInResults,
      vendorURL: synnexItem.VendorURL
    });
  }
}
